class ModularWorkflowEngine {
  constructor() {
    this.featuresRegistry = null;
    this.registryLoadPromise = null;
    this.isLoading = false;
  }

  async loadFeaturesRegistry() {
    if (this.featuresRegistry) {
      return this.featuresRegistry;
    }

    if (this.registryLoadPromise) {
      return this.registryLoadPromise;
    }

    this.isLoading = true;
    this.registryLoadPromise = (async () => {
      try {
        const response = await fetch('/api/core-features-registry/features-registry');
        if (!response.ok) {
          throw new Error(`Failed to fetch features registry: HTTP ${response.status}`);
        }
        const data = await response.json();
        if (!data.features || Object.keys(data.features).length === 0) {
          throw new Error('Features registry returned empty data');
        }
        this.featuresRegistry = data.features;
        console.log('✅ ModularWorkflowEngine: Features registry loaded from API', {
          featureCount: Object.keys(this.featuresRegistry).length
        });
        return this.featuresRegistry;
      } catch (error) {
        console.error('❌ ModularWorkflowEngine: CRITICAL ERROR loading features registry:', error);
        this.isLoading = false;
        this.registryLoadPromise = null;
        // NO FALLBACK - throw the error to make failure visible
        throw new Error(`CRITICAL: Cannot load features registry - ${error.message}`);
      } finally {
        this.isLoading = false;
      }
    })();

    return this.registryLoadPromise;
  }

  setFeaturesRegistry(registry) {
    this.featuresRegistry = registry;
    console.log('✅ ModularWorkflowEngine: Features registry set manually', {
      featureCount: registry ? Object.keys(registry).length : 0
    });
  }

  getFeatureRequirements(featureId) {
    if (this.featuresRegistry && this.featuresRegistry[featureId]) {
      const feature = this.featuresRegistry[featureId];
      if (feature.generation_requirements) {
        return {
          requires: feature.generation_requirements.workflow_steps || [],
          generates: feature.generation_requirements.outputs || []
        };
      }
    }

    console.warn(`⚠️ No generation_requirements found for feature: ${featureId}`);
    return { requires: [], generates: [] };
  }

  analyzeRequiredSteps(enabledFeatures) {
    console.log('🔍 ModularWorkflowEngine: Analyzing required steps for features:', enabledFeatures);

    const requiredSteps = new Set();
    const willGenerate = new Set();

    enabledFeatures.forEach(featureId => {
      const requirement = this.getFeatureRequirements(featureId);
      
      if (requirement) {
        requirement.requires.forEach(step => requiredSteps.add(step));
        requirement.generates.forEach(output => willGenerate.add(output));
      }
    });

    const executionPlan = {
      steps: Array.from(requiredSteps),
      outputs: Array.from(willGenerate),
      skipSteps: this.getSkippableSteps(requiredSteps),
      isFullStrategy: requiredSteps.size === 4,
      isQuickPlan: requiredSteps.size <= 2
    };

    console.log('📋 Execution Plan:', {
      requiredSteps: executionPlan.steps,
      skipSteps: executionPlan.skipSteps,
      outputCount: executionPlan.outputs.length,
      planType: executionPlan.isFullStrategy ? 'Full Strategy' : executionPlan.isQuickPlan ? 'Quick Strategy' : 'Custom'
    });

    return executionPlan;
  }

  getSkippableSteps(requiredSteps) {
    const allSteps = ['deep_analysis', 'contextual_research', 'custom_programs', 'synthesis'];
    return allSteps.filter(step => !requiredSteps.has(step));
  }

  async createWorkflowConfig(coreProductId, workflowRegistry) {
    // Ensure registry is loaded
    await this.loadFeaturesRegistry();

    const workflow = workflowRegistry.getWorkflow(coreProductId);
    
    if (!workflow || !workflow.uiComponents) {
      console.warn('⚠️ No workflow config found for:', coreProductId);
      return null;
    }

    const enabledFeatures = workflow.uiComponents.enabledFeatures || [];
    const executionPlan = this.analyzeRequiredSteps(enabledFeatures);

    return {
      workflowId: coreProductId,
      enabledFeatures,
      executionPlan,
      uiComponents: workflow.uiComponents,
      estimatedDuration: this.estimateDuration(executionPlan)
    };
  }

  estimateDuration(executionPlan) {
    const stepDurations = {
      deep_analysis: 60,
      contextual_research: 90,
      custom_programs: 120,
      synthesis: 45
    };

    const totalSeconds = executionPlan.steps.reduce((total, step) => {
      return total + (stepDurations[step] || 30);
    }, 0);

    const minutes = Math.ceil(totalSeconds / 60);
    
    if (minutes < 2) return `${totalSeconds} seconds`;
    if (minutes < 5) return `${minutes} minutes`;
    return `${minutes} minutes`;
  }

  shouldSkipStep(stepId, executionPlan) {
    return executionPlan.skipSteps.includes(stepId);
  }

  getRequiredOutputs(featureId) {
    const requirement = this.getFeatureRequirements(featureId);
    return requirement ? requirement.generates : [];
  }

  mapFeaturesToCapabilities(enabledFeatures) {
    const capabilities = new Set();
    
    enabledFeatures.forEach(featureId => {
      if (this.featuresRegistry && this.featuresRegistry[featureId]) {
        const feature = this.featuresRegistry[featureId];
        if (feature.capabilities) {
          feature.capabilities.forEach(cap => capabilities.add(cap));
        }
      }
    });

    return Array.from(capabilities);
  }

  getOptimizedWorkflowSteps(enabledFeatures) {
    const executionPlan = this.analyzeRequiredSteps(enabledFeatures);
    
    const stepMap = {
      deep_analysis: {
        id: 'deep_analysis',
        title: 'Deep Challenge Analysis',
        description: 'Analyzing your unique challenge context',
        status: 'pending',
        agentKey: 'DESIGN_LEAD'
      },
      contextual_research: {
        id: 'contextual_research',
        title: 'Contextual Research Planning',
        description: 'Creating tailored research approaches',
        status: 'pending',
        agentKey: 'UX_RESEARCH'
      },
      custom_programs: {
        id: 'custom_programs',
        title: 'Custom Program Generation',
        description: 'Creating bespoke programs and activities',
        status: 'pending',
        agentKey: 'UX_DESIGN_LEAD'
      },
      synthesis: {
        id: 'synthesis',
        title: 'Strategy Synthesis',
        description: 'Integrating elements into cohesive strategy',
        status: 'pending',
        agentKey: 'DESIGN_LEAD'
      }
    };

    return executionPlan.steps.map(stepId => stepMap[stepId]).filter(Boolean);
  }
}

const modularWorkflowEngine = new ModularWorkflowEngine();
export default modularWorkflowEngine;
